
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Solicitud } from './types';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';

interface SolicitudesResponse {
  data: Solicitud[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const fmt = (n: number | null) =>
  n === null ? '---' : `$${n.toLocaleString('es-CL')}`;

function StatusBadge({ estado }: { estado: Solicitud['estado'] }) {
  if (estado === 'revision') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-600 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
        <span className="material-symbols-outlined text-[14px]">pending</span>
        En Revisión
      </span>
    );
  }
  if (estado === 'aprobada') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800">
        <span className="material-symbols-outlined text-[14px]">verified</span>
        Aprobada
      </span>
    );
  }
  // Post-aprobación: el comercio ya recibió la plata. Cobranza interna no se expone.
  if (
    estado === 'abonada' ||
    estado === 'desembolsada' ||
    estado === 'en_cobro' ||
    estado === 'cobrada' ||
    estado === 'abandonada'
  ) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800">
        <span className="material-symbols-outlined text-[14px]">check_circle</span>
        Abonada
      </span>
    );
  }
  if (estado === 'cancelada') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700">
        <span className="material-symbols-outlined text-[14px]">do_not_disturb_on</span>
        Cancelada
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-50 text-red-600 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
      <span className="material-symbols-outlined text-[14px]">cancel</span>
      Rechazada
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr>
      <td className="px-6 py-4"><div className="h-4 w-28 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
      <td className="px-6 py-4">
        <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-1" />
        <div className="h-3 w-16 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
      </td>
      <td className="px-6 py-4"><div className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
      <td className="px-6 py-4"><div className="h-6 w-24 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" /></td>
      <td className="px-6 py-4 text-right"><div className="h-7 w-20 bg-slate-200 dark:bg-slate-700 rounded animate-pulse ml-auto" /></td>
    </tr>
  );
}

export default function MisSolicitudes() {
  const [searchId, setSearchId] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [estadoFilter, setEstadoFilter] = useState('');
  const [fechaFilter, setFechaFilter] = useState<Date | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [page, setPage] = useState(1);

  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 8, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);

  // Debounce de búsqueda por ID (500ms)
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchId);
      setPage(1);
    }, 500);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchId]);

  // Reset página al cambiar filtros
  useEffect(() => { setPage(1); }, [estadoFilter, fechaFilter]);

  const fetchSolicitudes = useCallback(async () => {
    setLoading(true);
    try {
      const baseUrl = getEndpointUrl('adelantos', 'solicitudes');
      const params = new URLSearchParams({ page: String(page), pageSize: '8' });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (estadoFilter) params.set('estado', estadoFilter);
      if (fechaFilter) {
        const dateStr = format(fechaFilter, 'yyyy-MM-dd');
        params.set('fechaDesde', dateStr);
        params.set('fechaHasta', dateStr);
      }
      const url = `${baseUrl}?${params.toString()}`;
      const data = await httpClient.get<SolicitudesResponse>(url);
      setSolicitudes(data.data);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Error al cargar solicitudes:', err);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, estadoFilter, fechaFilter]);

  useEffect(() => {
    fetchSolicitudes();
  }, [fetchSolicitudes]);

  const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
  const to = Math.min(pagination.page * pagination.pageSize, pagination.total);

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-end gap-4 mb-8 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
        <div className="w-full lg:w-48">
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">ID Solicitud</label>
          <div className="relative">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">
              search
            </span>
            <input
              type="text"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              placeholder="Buscar ID..."
              className="w-full h-10 pl-10 pr-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary"
            />
          </div>
        </div>
        <div className="w-full lg:w-48">
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Fecha Solicitud</label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button className="w-full h-10 flex items-center gap-2 pl-3 pr-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-left hover:border-slate-300 dark:hover:border-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary">
                <span className="material-symbols-outlined text-slate-400 text-[18px] flex-shrink-0">calendar_month</span>
                <span className={`flex-1 truncate ${fechaFilter ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>
                  {fechaFilter ? format(fechaFilter, 'dd/MM/yyyy') : 'Seleccionar fecha'}
                </span>
                {fechaFilter && (
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); setFechaFilter(undefined); }}
                    className="material-symbols-outlined text-slate-400 hover:text-slate-600 text-[18px] flex-shrink-0 cursor-pointer"
                  >
                    close
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={fechaFilter}
                onSelect={(date) => { setFechaFilter(date); setCalendarOpen(false); }}
                locale={es}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="w-full lg:w-48">
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Estado</label>
          <select
            value={estadoFilter}
            onChange={(e) => setEstadoFilter(e.target.value)}
            className="w-full h-10 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary px-3"
          >
            <option value="">Todos los estados</option>
            <option value="requested">En Revisión</option>
            <option value="approved">Aprobada</option>
            <option value="disbursed">Desembolsada</option>
            <option value="in_collection">En cobro</option>
            <option value="collected">Cobrada</option>
            <option value="abandoned">Incobrable</option>
            <option value="cancelled">Cancelada</option>
          </select>
        </div>
        {(searchId !== '' || fechaFilter !== undefined || estadoFilter !== '') && (
          <button
            onClick={() => { setSearchId(''); setFechaFilter(undefined); setEstadoFilter(''); }}
            className="h-10 px-4 rounded-lg text-sm font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-2 ml-auto"
          >
            <span className="material-symbols-outlined text-[18px]">filter_alt_off</span>
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800 text-slate-500 text-xs font-semibold uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">
              <th className="px-6 py-4">ID Solicitud</th>
              <th className="px-6 py-4">Fecha</th>
              <th className="px-6 py-4">Monto Total</th>
              <th className="px-6 py-4">Estado</th>
              <th className="px-6 py-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : solicitudes.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                  No se encontraron solicitudes para los filtros seleccionados.
                </td>
              </tr>
            ) : (
              solicitudes.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm text-slate-600 dark:text-slate-400">{s.id}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900 dark:text-white font-medium">{s.fecha}</div>
                    <div className="text-xs text-slate-500">{s.hora}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`text-sm font-bold ${
                        s.estado === 'rechazada'
                          ? 'text-slate-400 line-through'
                          : s.estado === 'revision'
                          ? 'text-slate-500 dark:text-slate-400'
                          : 'text-slate-900 dark:text-white'
                      }`}
                    >
                      {fmt(s.montoTotal)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge estado={s.estado} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link
                      to={`/adelantos/detalle?id=${encodeURIComponent(s.id.replace('#', ''))}`}
                      className="text-xs font-bold text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors px-3 py-1.5 rounded bg-slate-100 dark:bg-slate-800 hover:bg-primary/10"
                    >
                      Ver Detalle
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-8 flex justify-between items-center border-t border-slate-100 dark:border-slate-800 pt-6">
        <div className="text-sm text-slate-500">
          {loading ? (
            <span className="inline-block h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
          ) : pagination.total === 0 ? (
            'Sin resultados'
          ) : (
            <>Mostrando {from}–{to} de {pagination.total} solicitudes</>
          )}
        </div>
        {pagination.totalPages > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Pág. {pagination.page} de {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages || loading}
              className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
